import { InjectionToken } from '@angular/core';

import { JsonSchema } from '../model/json-schema';

/**
 * Where an app loads the schema documents its references name, by the key each is stored under. The app decides the
 * rest: the endpoint, which variant a key answers with — it sends the persona and the device along — and whether it
 * keeps a document once loaded, as `schemaDocumentCache` does (672942). It lists no documents, so it is no registry:
 * it answers a key.
 */
export interface SchemaDocumentLoader {
  /**
   * The document stored under `key`. `abortSignal` aborts once the load that asked is superseded or its injector is
   * destroyed — possibly before `load` is called, so a loader that honours it checks `aborted` first. Honouring it is
   * optional, as with `schemaLoader`'s `load`: a request that skips it runs to completion.
   */
  load(key: string, abortSignal?: AbortSignal): Promise<JsonSchema>;

  /** Loads every lazy and preload reference with its page too — for tests, and for comparing the loading scenarios. */
  readonly forceEager?: boolean;

  /**
   * Answers a key it loaded before without a request, as `schemaDocumentCache` does. Only such a loader preloads: a
   * preload reference's part is asked for with its document and again when its node shows, so with any other loader it
   * loads with its document, as an eager one does (672942).
   */
  readonly keepsDocuments?: boolean;
}

/** Provided where schemas may hold references; `schemaLoader` composes every schema it loads through it (670357). */
export const SCHEMA_DOCUMENT_LOADER = new InjectionToken<SchemaDocumentLoader>('SCHEMA_DOCUMENT_LOADER');
